/**
 * @jsx jsx
 */

/* eslint-disable */
import React from 'react';
import { token } from '@atlaskit/tokens';
import { Box } from '@atlaskit/primitives/compiled';
import ShortcutIcon from '@atlaskit/icon/core/migration/link-external--shortcut';
import { cssMap, jsx } from '@compiled/react';
import {
	analyticsAttributes,
	getItemAnalyticsContext,
	NavigationAnalyticsContext,
	withAnalyticsContextData,
} from '../../common/utils/analytics';
import { FadeIn } from './fade-in';
import { type SwitcherItemType } from '../../common/utils/links';
import ItemLink from './item-link';
import { AnalyticsItemType } from '../../types';
import Heading from '@atlaskit/heading';
import { useIsSwitcherTeam25TestCohort } from '../components/experiment-provider';

type SectionProps = {
	sectionId: string;
	title: React.ReactNode;
	titleLink?: SwitcherItemType;
	children?: React.ReactNode;
	actionSubject?: string;
	sectionRole?: string;
	ariaLabel?: string;
};

type SectionAnalyticsContext = {
	attributes: {
		group: string;
		groupItemsCount: number;
	};
};

const styles = cssMap({
	sectionBase: {
		paddingTop: token('space.075'),
		paddingRight: 0,
		paddingBottom: token('space.075'),
		paddingLeft: 0,
	},
	sectionVisualRefresh: {
		paddingTop: token('space.100'),
		overflow: 'hidden',
		marginTop: '-1px',
		borderTop: `1px solid ${token('color.border')}`,
	},
	list: {
		listStyleType: 'none',
		padding: 0,
		margin: 0,
	},
	box: {
		marginLeft: token('space.100'),
		marginRight: token('space.100'),
		marginTop: '0',
		marginBottom: token('space.100'),
	},
	titleBase: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		textTransform: 'uppercase',
		whiteSpace: 'nowrap',
		marginTop: token('space.200'),
		marginLeft: token('space.100'),
		color: token('color.text.subtle'),
		font: token('font.body.small'),
		fontWeight: token('font.weight.bold'),
		marginBottom: token('space.050'),
	},
	titleRefresh: {
		marginTop: token('space.075'),
		marginLeft: token('space.100'),
		marginBottom: token('space.075'),
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		color: token('color.text.subtlest'),
		fontWeight: token('font.weight.medium'),
	},
});

export const SectionContainerTitle = ({ title }: Pick<SectionProps, 'title'>) => {
	const isVisualRefreshEnabled = useIsSwitcherTeam25TestCohort();

	return (
		<Heading as="h2" size="xxsmall">
			<Box xcss={isVisualRefreshEnabled ? styles.titleRefresh : styles.titleBase}>{title}</Box>
		</Heading>
	);
};

const SectionContainerComponent = ({
	sectionId,
	title,
	titleLink,
	children,
	actionSubject,
	sectionRole,
	ariaLabel,
}: SectionProps) => {
	const isVisualRefreshEnabled = useIsSwitcherTeam25TestCohort();
	const hasChildren = React.Children.toArray(children).some(Boolean);
	if (!hasChildren) {
		return null;
	}

	return (
		<section
			css={[isVisualRefreshEnabled ? styles.sectionVisualRefresh : styles.sectionBase]}
			data-testid={`${sectionId}__section`}
			role={sectionRole}
			aria-label={ariaLabel}
		>
			<Box xcss={isVisualRefreshEnabled ? styles.box : undefined}>
				<FadeIn>
					{title && <SectionContainerTitle title={title} />}
					{titleLink && <TitleLink titleLink={titleLink} actionSubject={actionSubject} />}
				</FadeIn>
				<ul css={styles.list}>{children}</ul>
			</Box>
		</section>
	);
};

const TitleLink = ({
	titleLink,
	actionSubject,
}: {
	titleLink: SwitcherItemType;
	actionSubject?: string;
}) => (
	<NavigationAnalyticsContext
		key={titleLink.key}
		data={getItemAnalyticsContext(
			0,
			titleLink.key,
			AnalyticsItemType.PRODUCT,
			titleLink.productType,
		)}
	>
		<ItemLink
			href={titleLink.href}
			iconAfter={<ShortcutIcon LEGACY_size="small" color="currentColor" label="" />}
			label={titleLink.label}
			testId="section-title__link"
			actionSubject={actionSubject}
			ariaLabel={titleLink.ariaLabel}
		/>
	</NavigationAnalyticsContext>
);

export const SectionContainer = withAnalyticsContextData<SectionProps, SectionAnalyticsContext>(
	(props) =>
		analyticsAttributes({
			group: props.sectionId,
			groupItemsCount: React.Children.toArray(props.children).filter(React.isValidElement).length,
		}),
)(SectionContainerComponent);
/* eslint-enable */
